import React, { useContext, useState } from 'react';
import {
    DefaultButton,
    Icon,
    Modal,
    PrimaryButton,
    Stack,
    Text,
    useTheme,
} from '@fluentui/react';
import {
    CapabilityActionType,
    CapabilityDispatch,
} from '../../../Common/components/Capabilities/capabilityReducer';
import { useUserRoles } from '../../../Common/hooks/useUserRoles';

interface IUserRolesModalProps {
    isOpen: boolean;
    onDismiss: () => void;
}

const adminFeatures = [
    'Administration page (Cloud Status, Environment Data, DRI Tools)',
    'Deployment Steps pivot tab',
    'Kusto query viewer',
    'Build debug fields (Caching Enabled, Storage Upload, Required On Share)',
    'In Cloud URL viewer',
    'Egress History viewer',
    'Admin-only actions on Releases, Deployments, and Builds',
    'AGRM context menu actions',
];

export const UserRolesModal: React.FC<IUserRolesModalProps> = (props) => {
    const theme = useTheme();
    const capabilityDispatch = useContext(CapabilityDispatch);
    const [featuresExpanded, setFeaturesExpanded] = useState(false);

    const nonAdminMode = JSON.parse(
        localStorage.getItem('nonAdminMode') ?? 'false'
    );

    const userRoles = useUserRoles();

    // Check actual JWT roles for Srm.Admin (not affected by nonAdminMode override)
    const hasSrmAdminRole = userRoles.includes('Srm.Admin');

    const handleToggleNonAdmin = () => {
        capabilityDispatch({
            type: CapabilityActionType.toggleNonAdminMode,
        });
        props.onDismiss();
    };

    return (
        <Modal
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            styles={{
                main: {
                    maxWidth: 480,
                    minWidth: 360,
                    borderRadius: 8,
                    padding: 0,
                },
            }}
        >
            <Stack
                styles={{
                    root: {
                        padding: '20px 24px',
                        borderBottom: `1px solid ${theme.palette.neutralLight}`,
                    },
                }}
                horizontal
                horizontalAlign="space-between"
                verticalAlign="center"
            >
                <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
                    Your Roles
                </Text>
                <Icon
                    iconName="ChromeClose"
                    styles={{
                        root: {
                            cursor: 'pointer',
                            fontSize: 14,
                            color: theme.palette.neutralSecondary,
                            ':hover': {
                                color: theme.palette.neutralDark,
                            },
                        },
                    }}
                    onClick={props.onDismiss}
                />
            </Stack>

            <Stack
                styles={{ root: { padding: '16px 24px' } }}
                tokens={{ childrenGap: 16 }}
            >
                {/* Role list */}
                <Stack tokens={{ childrenGap: 6 }}>
                    {userRoles.length > 0 ? (
                        userRoles.map((role) => (
                            <Stack
                                key={role}
                                horizontal
                                verticalAlign="center"
                                tokens={{ childrenGap: 8 }}
                                styles={{
                                    root: {
                                        padding: '8px 12px',
                                        backgroundColor:
                                            theme.palette.neutralLighter,
                                        borderRadius: 4,
                                    },
                                }}
                            >
                                <Icon
                                    iconName="Permissions"
                                    styles={{
                                        root: {
                                            fontSize: 14,
                                            color: theme.palette.themePrimary,
                                        },
                                    }}
                                />
                                <Text>{role}</Text>
                            </Stack>
                        ))
                    ) : (
                        <Text
                            styles={{
                                root: {
                                    color: theme.palette.neutralSecondary,
                                    fontStyle: 'italic',
                                },
                            }}
                        >
                            No app roles assigned
                        </Text>
                    )}
                </Stack>

                {/* Non-admin mode section — only for users with Srm.Admin role */}
                {hasSrmAdminRole && (
                    <Stack
                        tokens={{ childrenGap: 12 }}
                        styles={{
                            root: {
                                borderTop: `1px solid ${theme.palette.neutralLight}`,
                                paddingTop: 16,
                            },
                        }}
                    >
                        {nonAdminMode && (
                            <Stack
                                horizontal
                                verticalAlign="center"
                                tokens={{ childrenGap: 8 }}
                                styles={{
                                    root: {
                                        padding: '8px 12px',
                                        backgroundColor:
                                            theme.palette.themeLighterAlt,
                                        borderRadius: 4,
                                        border: `1px solid ${theme.palette.themeLighter}`,
                                    },
                                }}
                            >
                                <Icon
                                    iconName="Info"
                                    styles={{
                                        root: {
                                            color: theme.palette.themePrimary,
                                            fontSize: 14,
                                        },
                                    }}
                                />
                                <Text
                                    styles={{
                                        root: {
                                            color: theme.palette.themePrimary,
                                            fontSize: 13,
                                        },
                                    }}
                                >
                                    Non-admin mode is active. Admin features are
                                    hidden.
                                </Text>
                            </Stack>
                        )}

                        {/* Collapsible admin features list */}
                        <Stack tokens={{ childrenGap: 8 }}>
                            <Stack
                                horizontal
                                verticalAlign="center"
                                tokens={{ childrenGap: 6 }}
                                styles={{
                                    root: {
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                    },
                                }}
                                onClick={() =>
                                    setFeaturesExpanded(!featuresExpanded)
                                }
                            >
                                <Icon
                                    iconName={
                                        featuresExpanded
                                            ? 'ChevronDown'
                                            : 'ChevronRight'
                                    }
                                    styles={{
                                        root: {
                                            fontSize: 12,
                                            transition: 'transform 0.2s',
                                        },
                                    }}
                                />
                                <Text
                                    styles={{
                                        root: {
                                            fontWeight: 600,
                                            fontSize: 13,
                                        },
                                    }}
                                >
                                    Admin-only features
                                </Text>
                            </Stack>

                            {featuresExpanded && (
                                <Stack
                                    tokens={{ childrenGap: 4 }}
                                    styles={{
                                        root: {
                                            paddingLeft: 20,
                                        },
                                    }}
                                >
                                    {adminFeatures.map((feature) => (
                                        <Stack
                                            key={feature}
                                            horizontal
                                            verticalAlign="start"
                                            tokens={{ childrenGap: 8 }}
                                        >
                                            <Text
                                                styles={{
                                                    root: {
                                                        color: theme.palette
                                                            .themePrimary,
                                                        fontSize: 13,
                                                        lineHeight: '20px',
                                                    },
                                                }}
                                            >
                                                •
                                            </Text>
                                            <Text
                                                styles={{
                                                    root: {
                                                        fontSize: 13,
                                                        lineHeight: '20px',
                                                    },
                                                }}
                                            >
                                                {feature}
                                            </Text>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                        </Stack>

                        <PrimaryButton
                            text={
                                nonAdminMode
                                    ? 'Exit non-admin mode'
                                    : 'Enter non-admin mode'
                            }
                            iconProps={{
                                iconName: nonAdminMode ? 'Admin' : 'UserRemove',
                            }}
                            onClick={handleToggleNonAdmin}
                            styles={{
                                root: { borderRadius: 4 },
                            }}
                        />
                    </Stack>
                )}
            </Stack>

            {/* Footer */}
            <Stack
                horizontal
                horizontalAlign="end"
                styles={{
                    root: {
                        padding: '12px 24px',
                        borderTop: `1px solid ${theme.palette.neutralLight}`,
                    },
                }}
            >
                <DefaultButton text="Close" onClick={props.onDismiss} />
            </Stack>
        </Modal>
    );
};
